import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { BrandRegistrationApplication } from '../../../app/models/BrandRegistration';
import useBrandRegistrationVerificationService from '../../../app/services/hooks/useBrandRegistrationVerificationService';
import useBrandFeasibilityService from '../../../app/services/hooks/useBrandFeasibilityService';
import InputField from '../../../components/InputField/InputField';
import SelectField from '../../../components/SelectField/SelectField';
import TextAreaField from '../../../components/TextAreaField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import SquareCheckbox from '../../../components/SquareCheckbox/SquareCheckbox';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import BrandFeasibilityResultModal from '../../../components/BrandFeasibilityResultModal';

interface VerificationStepProps {
    brandRegistrationId: number;
    applicationData: BrandRegistrationApplication;
    isEditable: boolean;
    onUpdate: () => void;
    paymentManagedByFirm?: boolean;  // Caso de un estudio: el pago lo acuerda el estudio con su cliente
}

const VerificationStep: React.FC<VerificationStepProps> = ({ 
    brandRegistrationId, 
    applicationData, 
    paymentManagedByFirm = false,
    isEditable,
    onUpdate 
}) => {
    const { getVerificationByBrandRegistration, storeVerification, updateVerification, markAsPaid, fetchingStoreVerification, fetchingUpdateVerification, fetchingMarkAsPaid } = useBrandRegistrationVerificationService();
    const { evaluateFeasibility, fetchingEvaluateFeasibility } = useBrandFeasibilityService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();
    const [verificationData, setVerificationData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showFeasibilityModal, setShowFeasibilityModal] = useState(false);
    const [feasibilityResult, setFeasibilityResult] = useState<any>(null);
    
     // Form data
     const [formData, setFormData] = useState({
         result: '', // 'approved' o 'rejected'
         observations: '',
         classes: [] as any[],
         feasibility_details: '',
         internal_payment_agreement: false, // Pago acordado internamente
         additional_classes: [
             { enabled: true, class: '', tariff: '1', isFree: false } // Primera clase 1 UTM (editable)
         ] as Array<{enabled: boolean, class: string, tariff: string, isFree: boolean}>
     });

    const handleDisabledChange = () => {
        // No hace nada porque el campo está deshabilitado
    };

    const handleNoOp = () => {
        // Función helper para campos deshabilitados - no hace nada
        return;
    };

    const handleMarkAsPaid = () => {
        if (!verificationData || !verificationData.id) return;

        requestConfirmation({
            title: '¿Marcar verificación como pagada?',
            message: 'Esta acción marcará la verificación como pagada y permitirá al usuario continuar con la Etapa 3 (Solicitud INAPI). ¿Confirma que el pago fue recibido?',
            buttons: {
                confirmButton: {
                    label: 'Sí, marcar como pagada',
                    onClick: () => {
                        markAsPaid(verificationData.id, {
                            onSuccess: (response) => {
                                toast.success(response.message || 'Verificación marcada como pagada exitosamente');
                                loadVerification();
                                onUpdate();
                            },
                            onError: (response) => {
                                toast.error(response.message || 'Error al marcar como pagada');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // No hacer nada, solo cerrar el modal
                    }
                }
            }
        });
    };

    useEffect(() => {
        loadVerification();
    }, [brandRegistrationId]);

    const loadVerification = () => {
        getVerificationByBrandRegistration(brandRegistrationId, {
            onSuccess: (response) => {
                if (response.data && response.data.verification) {
                    const verification = response.data.verification;
                    setVerificationData(verification);
                    
                    // Mapear feasibility_result a result para el frontend
                    const result = verification.feasibility_result === 'positive' ? 'approved' : 
                                   verification.feasibility_result === 'negative' ? 'rejected' : '';
                    
                    // Mapear las clases del backend al formato del frontend
                    const mappedClasses = verification.classes && verification.classes.length > 0
                        ? verification.classes.map((cls: any) => ({
                            enabled: true,
                            class: cls.class_name || cls.class_description || '',
                            tariff: cls.fee ? cls.fee.toString() : '1',
                            isFree: false
                        }))
                        : [{ enabled: true, class: '', tariff: '1', isFree: false }];
                    
                    setFormData({
                        result: result,
                        observations: verification.observations || '',
                        classes: verification.classes || [],
                        feasibility_details: verification.feasibility_detail || '',
                        internal_payment_agreement: verification.internal_payment_agreement || false,
                        additional_classes: mappedClasses
                    });
                    setIsEditMode(false);
                } else {
                    // No hay verificación, mostrar formulario vacío
                    setIsEditMode(true);
                }
                setLoading(false);
            },
            onError: () => {
                // Error al cargar, probablemente no existe, mostrar formulario vacío
                setIsEditMode(true);
                setLoading(false);
            }
        });
    };

     // Clic en la tarjeta (fuera de la casilla). Los clics en la casilla o su texto los
    // maneja el checkbox: si la tarjeta también los procesara, el clic que el navegador
    // reenvía del <label> al <input> desmarcaba la opción recién marcada.
    const selectResultFromCard = (e: React.MouseEvent, value: string) => {
        if ((e.target as HTMLElement).closest('label, input')) return;
        handleInputChange('result', value);
    };

    const handleInputChange = (field: string, value: any) => {
         setFormData(prev => ({
             ...prev,
             [field]: value
         }));
     };

     const addClass = () => {
         setFormData(prev => ({
             ...prev,
             additional_classes: [
                 ...prev.additional_classes,
                 { enabled: true, class: '', tariff: '1', isFree: false }
             ]
         }));
     };

     const removeClass = (index: number) => {
         // No permitir eliminar la primera clase
         if (index === 0) return;
         
         setFormData(prev => ({
             ...prev,
             additional_classes: prev.additional_classes.filter((_, i) => i !== index)
         }));
     };

    const updateClass = (index: number, field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            additional_classes: prev.additional_classes.map((cls, i) => 
                i === index ? { ...cls, [field]: value } : cls
            )
        }));
    };
    
    const handleEvaluateFeasibility = () => {
        if (!applicationData?.brand_name) {
            toast.error('No se encontró el nombre de la marca a evaluar');
            return;
        }
        
        // Usar las clases del formulario si están disponibles
        const classes = formData.additional_classes
            .filter(cls => cls.class && cls.class.trim() !== '')
            .map(cls => cls.class);
        
        // Obtener descripción de la marca (services_description)
        const brandDescription = applicationData?.services_description || '';
        
        evaluateFeasibility(applicationData.brand_name, brandDescription, classes, {
            onSuccess: (response) => {
                setFeasibilityResult(response.data);
                setShowFeasibilityModal(true);
                toast.success('Análisis de factibilidad completado');
            },
            onError: (response) => {
                toast.error(response.message || 'Error al evaluar factibilidad');
            }
        });
    };

    const handleSubmit = () => {
        // Validaciones básicas
        if (!formData.result) {
            toast.error('Debe seleccionar un resultado de verificación');
            return;
        }

        // Validar detalle si la factibilidad es negativa
        const isNegative = formData.result === 'rejected';
        if (isNegative && (!formData.feasibility_details || formData.feasibility_details.trim() === '')) {
            toast.error('El detalle de factibilidad es requerido cuando el resultado es negativo');
            setErrors({ feasibility_detail: ['El detalle de factibilidad es requerido cuando el resultado es negativo.'] });
            return;
        }

        // Preparar datos para el backend
        const backendData: any = {
            brand_registration_id: brandRegistrationId,
            feasibility_result: formData.result === 'approved' ? 'positive' : 'negative',
            internal_payment_agreement: formData.internal_payment_agreement || false,
            classes: formData.additional_classes.map((classItem, index) => ({
                class_name: classItem.class,
                class_description: classItem.class,
                fee: parseFloat(classItem.tariff) || 0,
                order: index + 1
            })).filter(cls => cls.class_name && cls.class_name.trim() !== '')
        };

        // Solo incluir feasibility_detail si tiene valor o si es negativo (requerido)
        if (formData.feasibility_details && formData.feasibility_details.trim() !== '') {
            backendData.feasibility_detail = formData.feasibility_details;
        } else if (isNegative) {
            // Si es negativo y no tiene detalle, el backend lo validará
            backendData.feasibility_detail = '';
        }

        // Determinar si es creación o actualización
        const isUpdate = verificationData && verificationData.id;


        // Ejecutar el servicio correspondiente
        (isUpdate 
            ? updateVerification(verificationData.id, backendData, {
                onSuccess: (response) => {
                    toast.success(response.message || 'Verificación actualizada correctamente.');
                    setErrors({});
                    setIsEditMode(false);
                    loadVerification();
                    onUpdate();
                },
                onError: (response) => {
                    if (response.status === 'fields_validation' && response.data) {
                        setErrors(response.data);
                        toast.error('Por favor corrige los errores en el formulario');
                    } else {
                        toast.error(response.message || 'Error al actualizar la verificación');
                    }
                },
                onFieldError: (response) => {
                    setErrors(response.data);
                }
            })
            : storeVerification(brandRegistrationId, backendData, {
                onSuccess: (response) => {
                    const successMessage = formData.internal_payment_agreement 
                        ? 'Verificación guardada correctamente. Pago acordado internamente (no se envió email al cliente).'
                        : 'Verificación guardada correctamente. Se ha enviado un email al cliente.';
                    toast.success(response.message || successMessage);
                    setErrors({});
                    loadVerification();
                    onUpdate();
                },
                onError: (response) => {
                    if (response.status === 'fields_validation' && response.data) {
                        setErrors(response.data);
                        toast.error('Por favor corrige los errores en el formulario');
                    } else {
                        toast.error(response.message || 'Error al guardar la verificación');
                    }
                },
                onFieldError: (response) => {
                    setErrors(response.data);
                }
            })
        );
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    // Si no tiene permisos de edición y hay datos, mostrar vista de solo lectura
    if (!isEditable && verificationData) {
        // Se mostrará la vista de solo lectura al final del componente
    } else if (!isEditable && !verificationData) {
        // Si no tiene permisos y no hay datos, mostrar formulario deshabilitado
        return (
            <div>
                <h4 className="mb-4">Verificación y Factibilidad</h4>
                
                
                <div className="row g-3">
                    {/* Resultado de Verificación */}
                    <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">Resultado de solicitud</h5>
                    </div>

                    {/* Factibilidad */}
                    <div className="col-12">
                        <label className="fw-bold mb-3">Factibilidad</label>
                        <p className="text-muted mb-3">Según el resultado obtenido, selecciona una de las opciones.</p>
                        
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div 
                                    className={`square-checkbox-card disabled`}
                                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                                >
                                    <SquareCheckbox
                                        id="approved"
                                        name="result"
                                        value="approved"
                                        checked={false}
                                        onChange={handleNoOp}
                                        label="Factibilidad positiva"
                                        disabled={true}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div 
                                    className={`square-checkbox-card disabled`}
                                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                                >
                                    <SquareCheckbox
                                        id="rejected"
                                        name="result"
                                        value="rejected"
                                        checked={false}
                                        onChange={handleNoOp}
                                        label="Factibilidad negativa"
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detalle de factibilidad */}
                    <div className="col-12 mt-4">
                        <TextAreaField
                            label="Detalle de factibilidad"
                            value=""
                            onChange={handleNoOp}
                            rows={4}
                            disabled={true}
                            placeholder="Escriba el detalle, de acuerdo al resultado obtenido."
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Mostrar formulario editable
    if (isEditMode || !verificationData) {
        return (
            <div>
                <h4 className="mb-4">Verificación y Factibilidad</h4>
                
                <div className="alert alert-info mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    Complete el formulario de verificación y factibilidad para esta solicitud.
                </div>

                <div className="row g-3">
                    {/* Resultado de Verificación */}
                    <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">Resultado de solicitud</h5>
                    </div>

                    {/* Factibilidad */}
                    <div className="col-12">
                        <label className="fw-bold mb-3">Factibilidad</label>
                        <p className="text-muted mb-3">Según el resultado obtenido, selecciona una de las opciones.</p>
                        
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div 
                                    className={`square-checkbox-card ${formData.result === 'approved' ? 'selected' : ''} ${!isEditable ? 'disabled' : ''}`}
                                    onClick={isEditable ? (e) => selectResultFromCard(e, 'approved') : undefined}
                                    style={!isEditable ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                                >
                                    <SquareCheckbox
                                        id="approved"
                                        name="result"
                                        value="approved"
                                        checked={formData.result === 'approved'}
                                        onChange={(value, checked) => isEditable && handleInputChange('result', checked ? value : '')}
                                        label="Factibilidad positiva"
                                        disabled={!isEditable}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div 
                                    className={`square-checkbox-card ${formData.result === 'rejected' ? 'selected' : ''} ${!isEditable ? 'disabled' : ''}`}
                                    onClick={isEditable ? (e) => selectResultFromCard(e, 'rejected') : undefined}
                                    style={!isEditable ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                                >
                                    <SquareCheckbox
                                        id="rejected"
                                        name="result"
                                        value="rejected"
                                        checked={formData.result === 'rejected'}
                                        onChange={(value, checked) => isEditable && handleInputChange('result', checked ? value : '')}
                                        label="Factibilidad negativa"
                                        disabled={!isEditable}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Botón de Evaluación con IA */}
                    {isEditable && (
                        <div className="col-12">
                            <div className="alert alert-primary d-flex align-items-center justify-content-between border-0 shadow-sm">
                                <div className="d-flex align-items-start">
                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                         style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                                        <i className="fas fa-robot" style={{ fontSize: '20px' }}></i>
                                    </div>
                                    <div>
                                        <h6 className="mb-1">Análisis de Factibilidad con Inteligencia Artificial</h6>
                                        <p className="mb-0 small text-muted">
                                            La IA buscará marcas similares registradas en INAPI y analizará posibles conflictos para ayudarte en tu evaluación.
                                        </p>
                                    </div>
                                </div>
                                <ButtonGeneral
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    onClick={handleEvaluateFeasibility}
                                    title="Evaluar con IA"
                                    icon="fas fa-magic"
                                    loading={fetchingEvaluateFeasibility}
                                    disabled={fetchingEvaluateFeasibility}
                                />
                            </div>
                        </div>
                    )}

                    {/* Detalle de factibilidad */}
                    <div className="col-12 mt-3">
                        <TextAreaField
                            label={`Detalle de factibilidad${formData.result === 'rejected' ? ' *' : ''}`}
                            value={formData.feasibility_details}
                            onChange={(value) => handleInputChange('feasibility_details', value)}
                            onFocus={() => onFocusRemove('feasibility_detail')}
                            rows={4}
                            disabled={!isEditable}
                            placeholder={formData.result === 'rejected' ? 'El detalle es obligatorio cuando la factibilidad es negativa.' : 'Escriba el detalle, de acuerdo al resultado obtenido.'}
                            error={fieldErrorMessage('feasibility_detail')}
                        />
                        {formData.result === 'rejected' && (
                            <small className="text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                Este campo es obligatorio cuando la factibilidad es negativa.
                            </small>
                        )}
                    </div>

                     {/* Tipos de clases */}
                     <div className="col-12 mt-4">
                         <div className="d-flex justify-content-between align-items-center mb-3">
                             <h5 className="mb-0">
                                 Tipos de clases 
                                 <i className="fas fa-question-circle ms-2 text-primary" style={{ fontSize: '0.8em' }}></i>
                             </h5>
                             {isEditable && (
                                 <ButtonGeneral
                                     type="button"
                                     variant="outline"
                                     size="small"
                                     onClick={addClass}
                                     title="Agregar Clase"
                                     icon="fas fa-plus"
                                 />
                             )}
                         </div>
                         <p className="text-muted mb-4">
                             Selecciona y escriba los tipos de clases elegidas de acuerdo a las características de la marca. 
                         </p>
                         
                         <div className="row g-3">
                             {formData.additional_classes.map((classItem, index) => (
                                 <div key={index} className="col-12">
                                             <div className="d-flex justify-content-between align-items-center mb-3">
                                                 {index > 0 && isEditable && (
                                                     <button
                                                         type="button"
                                                         className="btn btn-outline-danger btn-sm"
                                                         onClick={() => removeClass(index)}
                                                         style={{ padding: '4px 8px' }}
                                                     >
                                                         <i className="fas fa-trash me-1"></i>
                                                         Quitar
                                                     </button>
                                                 )}
                                             </div>
                                             
                                             <div className="row g-3">
                                                 <div className="col-md-8">
                                                     <InputField
                                                         label="Numero de la clase"
                                                         value={classItem.class}
                                                         onChange={(value) => updateClass(index, 'class', value)}
                                                         onFocus={() => onFocusRemove(`classes.${index}.class_name`, true)}
                                                         disabled={!isEditable}
                                                         placeholder="Ej: Productos farmacéuticos, servicios de consultoría, etc."
                                                         error={fieldErrorMessage(`classes.${index}.class_name`, true)}
                                                     />
                                                 </div>
                                                 <div className="col-md-4">
                                                     <InputField
                                                         label="Tarifa (UTM)"
                                                         type="number"
                                                         value={classItem.tariff}
                                                         onChange={(value) => updateClass(index, 'tariff', value)}
                                                         onFocus={() => onFocusRemove(`classes.${index}.fee`, true)}
                                                         disabled={false}
                                                         placeholder="Ingrese tarifa en UTM"
                                                         error={fieldErrorMessage(`classes.${index}.fee`, true)}
                                                     />
                                                 </div>
                                             </div>
                                 </div>
                             ))}
                         </div>
                     </div>

                    {/* Plan de Servicio Seleccionado - Solo lectura */}
                    {applicationData?.selected_pricing && (
                    <div className="col-12 mt-5">
                        <h5 className="mb-4">
                            <i className="fas fa-tag me-2 text-primary"></i>
                            Plan de Servicio Seleccionado
                        </h5>
                        
                        <div className="alert alert-info mb-0">
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <i className="fas fa-check-circle text-primary" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                                <div>
                                    <h6 className="mb-1" style={{ fontWeight: 600, color: '#001849' }}>
                                        {applicationData.selected_pricing.plan_name}
                                    </h6>
                                    <div>
                                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#001849' }}>
                                            ${applicationData.selected_pricing.base_price?.toLocaleString('es-CL')}
                                        </span>
                                        <span className="text-muted ms-1" style={{ fontSize: '0.9rem' }}>
                                            {applicationData.selected_pricing.currency || 'CLP'}
                                        </span>
                                    </div>
                                    {applicationData.selected_pricing.description && (
                                        <p className="text-muted small mb-0 mt-1">
                                            {applicationData.selected_pricing.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Continuidad del proceso - Solo visible si tiene permisos de edición */}
                    {isEditable && (
                    <div className="col-12 mt-4">
                        <h5 className="mb-4">
                            <i className="fas fa-route me-2 text-primary"></i>
                            Continuidad del proceso
                        </h5>
                        
                        {/* Checkbox para pago acordado internamente (no aplica a casos de estudio) */}
                        {paymentManagedByFirm ? (
                            <div className="alert alert-info mb-4" data-testid="payment-managed-by-firm">
                                <i className="fas fa-handshake me-2"></i>
                                Pago acordado internamente con el cliente. Cuando se concrete, márcalo como pagado para continuar.
                            </div>
                        ) : (
                            <div 
                                className={`p-4 rounded-3 mb-4 ${formData.internal_payment_agreement ? 'border border-2' : 'border'}`}
                                style={{
                                    backgroundColor: formData.internal_payment_agreement ? '#fff8e1' : '#f8f9fa',
                                    borderColor: formData.internal_payment_agreement ? '#ffc107' : '#dee2e6',
                                    transition: 'all 0.3s ease',
                                    boxShadow: formData.internal_payment_agreement ? '0 4px 12px rgba(255, 193, 7, 0.15)' : 'none'
                                }}
                            >
                                <div className="d-flex align-items-start">
                                    <div className="form-check d-flex align-items-center" style={{ minHeight: 'auto' }}>
                                        <input
                                            className="form-check-input mt-0"
                                            type="checkbox"
                                            id="internalPaymentAgreement"
                                            checked={formData.internal_payment_agreement || false}
                                            onChange={(e) => handleInputChange('internal_payment_agreement', e.target.checked)}
                                            disabled={!isEditable}
                                            style={{ 
                                                width: '1.75rem', 
                                                height: '1.75rem', 
                                                cursor: isEditable ? 'pointer' : 'not-allowed',
                                                flexShrink: 0,
                                                border: '2px solid #dee2e6',
                                                borderRadius: '0.375rem'
                                            }}
                                        />
                                    </div>
                                    <div className="ms-3 flex-grow-1">
                                        <label 
                                            className="form-check-label d-flex align-items-center mb-0" 
                                            htmlFor="internalPaymentAgreement"
                                            style={{ 
                                                cursor: isEditable ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            <span 
                                                className="d-flex align-items-center"
                                                style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '600',
                                                    color: formData.internal_payment_agreement ? '#001849' : '#495057'
                                                }}
                                            >
                                                <i 
                                                    className="fas fa-handshake me-2"
                                                    style={{ 
                                                        fontSize: '1.3rem',
                                                        color: formData.internal_payment_agreement ? '#ffc107' : '#6c757d'
                                                    }}
                                                ></i>
                                                Pago acordado internamente
                                            </span>
                                            {formData.internal_payment_agreement && (
                                                <span 
                                                    className="badge bg-warning text-dark ms-2" 
                                                    style={{ 
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600',
                                                        padding: '0.35rem 0.65rem'
                                                    }}
                                                >
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    Activo
                                                </span>
                                            )}
                                        </label>
                                        <div 
                                            className="mt-2"
                                            style={{ 
                                                fontSize: '0.9rem',
                                                lineHeight: '1.6',
                                                color: '#6c757d'
                                            }}
                                        >
                                            <div className="d-flex align-items-start mb-2">
                                                <i 
                                                    className="fas fa-info-circle mt-1 me-2" 
                                                    style={{ 
                                                        color: formData.internal_payment_agreement ? '#ffc107' : '#6c757d',
                                                        fontSize: '0.95rem',
                                                        flexShrink: 0
                                                    }}
                                                ></i>
                                                <span>
                                                    Al marcar esta opción, el pago se acordará directamente entre el abogado y el cliente sin pasar por la pasarela de pago.
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-start">
                                                <i 
                                                    className="fas fa-exclamation-triangle mt-1 me-2" 
                                                    style={{ 
                                                        color: '#dc3545',
                                                        fontSize: '0.95rem',
                                                        flexShrink: 0
                                                    }}
                                                ></i>
                                                <span>
                                                    <strong style={{ color: '#dc3545' }}>NO se enviará email de pago al cliente.</strong> El abogado podrá marcar manualmente como pagado cuando se concrete el acuerdo.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="bg-light rounded-3 p-4 mb-4">
                             <div className="d-flex align-items-center mb-3">
                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                     style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                    <i className="fas fa-info-circle" style={{ fontSize: '18px' }}></i>
                                 </div>
                                 <h6 className="mb-0 text-primary">Para continuar con el proceso de registro, debes solicitar al cliente:</h6>
                             </div>
                             
                             <div className="row g-4">
                                 <div className="col-md-4">
                                     <div className="d-flex align-items-start">
                                         <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                             style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                             <span className="fw-bold fs-6">1</span>
                                         </div>
                                         <div>
                                             <h6 className="mb-2">Pago del servicio</h6>
                                             <p className="text-muted small mb-0">
                                                 {paymentManagedByFirm
                                                     ? 'Acordado internamente con el cliente'
                                                     : 'Monere (si no posee un plan contratado)'}
                                             </p>
                                         </div>
                                     </div>
                                 </div>
                                 
                                 <div className="col-md-4">
                                     <div className="d-flex align-items-start">
                                         <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                             style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                             <span className="fw-bold fs-6">2</span>
                                         </div>
                                         <div>
                                             <h6 className="mb-2">Información adicional</h6>
                                             <p className="text-muted small mb-0">
                                                 Datos del solicitante y representante legal
                                             </p>
                                         </div>
                                     </div>
                                 </div>
                                 
                                 <div className="col-md-4">
                                     <div className="d-flex align-items-start">
                                         <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                             style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                             <span className="fw-bold fs-6">3</span>
                                         </div>
                                         <div>
                                             <h6 className="mb-2">Firma de poder</h6>
                                             <p className="text-muted small mb-0">
                                                 Documento de poder simple
                                             </p>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                         </div>
                         
                         {/* <div className="alert alert-info border-0 shadow-sm">
                             <div className="d-flex align-items-start">
                                <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0 mt-1" 
                                     style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                    <i className="fas fa-lightbulb" style={{ fontSize: '18px' }}></i>
                                 </div>
                                 <div>
                                     <h6 className="alert-heading mb-2">Nota importante</h6>
                                     <p className="mb-0">
                                         Si el cliente dispone de un plan contratado, solo se le requerirán pagos correspondientes a las 
                                         <strong> tarifas asociadas con INAPI</strong>, así como a las 
                                         <strong> clases adicionales elegidas</strong>, valoradas en UTM.
                                     </p>
                                 </div>
                             </div>
                         </div> */}
                     </div>
                     )}

                    {/* Botones de acción - Solo visibles si tiene permisos de edición */}
                    {isEditable && (
                    <div className="col-12 mt-4">
                        <div className="d-flex gap-2 justify-content-end">
                            {verificationData && (
                                <ButtonGeneral
                                    type="button"
                                    variant="outline"
                                    size="medium"
                                    onClick={() => {
                                        setIsEditMode(false);
                                        // Recargar los datos originales
                                        const result = verificationData.feasibility_result === 'positive' ? 'approved' : 
                                                      verificationData.feasibility_result === 'negative' ? 'rejected' : '';
                                        
                                        const mappedClasses = verificationData.classes && verificationData.classes.length > 0
                                            ? verificationData.classes.map((cls: any) => ({
                                                enabled: true,
                                                class: cls.class_name || cls.class_description || '',
                                                tariff: cls.fee ? cls.fee.toString() : '1',
                                                isFree: false
                                            }))
                                            : [{ enabled: true, class: '', tariff: '1', isFree: false }];
                                        
                                        setFormData({
                                            result: result,
                                            observations: verificationData.observations || '',
                                            classes: verificationData.classes || [],
                                            feasibility_details: verificationData.feasibility_detail || '',
                                            internal_payment_agreement: verificationData.internal_payment_agreement || false,
                                            additional_classes: mappedClasses
                                        });
                                    }}
                                    title="Cancelar"
                                    disabled={fetchingStoreVerification || fetchingUpdateVerification}
                                />
                            )}
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                onClick={handleSubmit}
                                title={verificationData ? "Actualizar Verificación" : "Mandar Solicitud"}
                                loading={fetchingStoreVerification || fetchingUpdateVerification}
                                disabled={fetchingStoreVerification || fetchingUpdateVerification}
                            />
                        </div>
                        <div className="text-center mt-2">
                            <small className="text-muted">
                                Cliente será notificado por correo y a través de Monere.
                            </small>
                        </div>
                    </div>
                    )}
                </div>
                
                {/* Modal de resultados de IA */}
                <BrandFeasibilityResultModal
                    show={showFeasibilityModal}
                    onHide={() => setShowFeasibilityModal(false)}
                    result={feasibilityResult}
                />
            </div>
        );
    }

    // Mostrar datos de verificación completada (solo lectura)
    return (
        <div>
            <h4 className="mb-4">Verificación y Factibilidad</h4>
            
            {isEditable && (
                <>
                    <div className="alert alert-success mb-4">
                        <i className="fas fa-check-circle me-2"></i>
                        Esta etapa ha sido completada exitosamente.
                    </div>
                    <div className="mb-3 d-flex gap-2">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={() => setIsEditMode(true)}
                            title="Editar Verificación"
                            icon="fas fa-edit"
                        />
                        
                        {/* Mostrar botón "Marcar como pagado" si tiene pago interno y no está pagado */}
                        {verificationData.internal_payment_agreement && verificationData.payment_status !== 'paid' && (
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                onClick={handleMarkAsPaid}
                                title="Marcar como Pagado"
                                icon="fas fa-check-circle"
                                loading={fetchingMarkAsPaid}
                                disabled={fetchingMarkAsPaid}
                            />
                        )}
                        
                        {/* Mostrar badge si está pagado */}
                        {verificationData.payment_status === 'paid' && (
                            <span className="badge bg-success align-self-center px-3 py-2">
                                <i className="fas fa-check-circle me-1"></i>
                                Pago Confirmado
                            </span>
                        )}
                    </div>
                </>
            )}
            
            {!isEditable && (
                <div className="alert alert-info mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    Vista de solo lectura. No tiene permisos para editar esta etapa.
                </div>
            )}

            <div className="row g-3">
                {/* Resultado de Verificación */}
                <div className="col-12">
                    <h5 className="border-bottom pb-2 mb-3">Resultado de Verificación</h5>
                </div>

                <div className="col-md-6">
                    <InputField
                        label="Resultado"
                        value={
                            verificationData.feasibility_result === 'positive' ? 'Factibilidad Positiva' :
                            verificationData.feasibility_result === 'negative' ? 'Factibilidad Negativa' :
                            ''
                        }
                        onChange={handleDisabledChange}
                        disabled={true}
                    />
                </div>

                <div className="col-md-6">
                    <InputField
                        label="Fecha de Verificación"
                        value={verificationData.created_at ? new Date(verificationData.created_at).toLocaleDateString('es-CL') : ''}
                        onChange={handleDisabledChange}
                        disabled={true}
                    />
                </div>

                <div className="col-12">
                    <TextAreaField
                        label="Detalle de factibilidad"
                        value={verificationData.feasibility_detail || ''}
                        onChange={handleDisabledChange}
                        rows={4}
                        disabled={true}
                    />
                </div>

                {/* Clases Verificadas */}
                {verificationData.classes && verificationData.classes.length > 0 && (
                    <>
                        <div className="col-12 mt-4">
                            <h5 className="border-bottom pb-2 mb-3">Clases Verificadas</h5>
                        </div>

                        <div className="col-12">
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Orden</th>
                                            <th>Descripción</th>
                                            <th>Tarifa (UTM)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {verificationData.classes.map((classItem: any, index: number) => (
                                            <tr key={index}>
                                                <td>{classItem.order || index + 1}</td>
                                                <td>{classItem.class_name || classItem.class_description || '-'}</td>
                                                <td>
                                                    <span className="badge bg-success">
                                                        {classItem.fee || 0} UTM
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Documentos */}
                {verificationData.documents && verificationData.documents.length > 0 && (
                    <>
                        <div className="col-12 mt-4">
                            <h5 className="border-bottom pb-2 mb-3">Documentos Adjuntos</h5>
                        </div>

                        <div className="col-12">
                            <ul className="list-group">
                                {verificationData.documents.map((doc: any, index: number) => (
                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>
                                            <i className="fas fa-file-pdf me-2 text-danger"></i>
                                            {doc.name}
                                        </span>
                                        <a 
                                            href={doc.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            <i className="fas fa-download me-1"></i>
                                            Descargar
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerificationStep;

